import _ from 'lodash'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { InputField } from 'Components/reusable/InputFields/InputField'
import { Id } from 'types/ids'
import { Context } from '../context'
import { AnyObject, ContextValue, EditableFieldsOfType, getFieldControlId } from '../types'

/**
 * Password field.
 */
export const DataEditContextNumberField = <T extends AnyObject>({
  field,
  noLabel,
  ...rest
}: { field: EditableFieldsOfType<T, number, Id>; noLabel?: boolean } & Omit<
  Record<string, unknown>,
  'onChange' | 'value' | 'error' | 'name'
>) => {
  const { flatData, errors, disabled, setFields, handleSubmit, localeBase, t } = useContext<ContextValue<T>>(Context)
  const controlId = getFieldControlId(field, rest.id as string | undefined, rest.testId as string | undefined)

  const [value, setValue] = useState<string>((flatData[field] as unknown as number)?.toString() ?? null)
  const [focused, setFocused] = useState<boolean>(false)
  const error = errors[field]

  const setField = useMemo(
    () =>
      _.debounce((newValue: string) => {
        const numberValue = parseFloat(newValue)
        setFields({ [field]: numberValue })
      }, 300),
    [setFields, field]
  )

  useEffect(() => {
    if (!focused) {
      setValue((flatData[field] as unknown as number)?.toString() ?? null)
    }
  }, [field, flatData, focused])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setValue(newValue)
    setField(newValue)
  }

  return (
    <InputField
      id={controlId}
      onKeyDown={e => handleSubmit(e)}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      type="number"
      value={value ?? null}
      error={error}
      disabled={disabled[field]}
      name={field}
      label={!noLabel ? t(`${localeBase}.${field}`) : undefined}
      {...rest}
    />
  )
}

/**
 * Number slider field.
 */
